import { ft } from '../core/units.js';
import { CART, NEAR, PLACING, POSSIBLE } from './prompts.js';
import { OBJECTS } from '../data/objects.js';

/**
 * What the action key offers at a cart: taking the handle, getting on,
 * letting go, standing one back up, moving a load between it and your arms —
 * and, for anything with a driver's seat, driving it and who rides along.
 *
 * Prompts only. Every one of these runs through the wheeled class and the
 * carry system; this decides which of them the key offers where you stand.
 */
export function createCartOffers({ wheeled, carry, riders, player, rig, hud, autosave, throwing = null }) {
  /**
   * The handle: taking it, letting go, getting on, and moving a load between a
   * cart and your arms.
   *
   * Resolved by proximity and facing like everything else, and it works on
   * whichever cart is nearest — the banquet one and the room service ones are
   * the same object with different numbers.
   */
  const CART_REACH = ft(6);

  /** How far behind the ball a cart can be and still be the one you are backing up to. */
  const TOW_REACH = ft(6);

  const name = (cart) => cart.definition.name.toLowerCase();
  const feet = (metres) => (metres / 0.3048).toFixed(1);

  /**
   * What somebody standing by the ball would call out: how far there is to
   * go, which way you are off, and by how many degrees. The prompt carries it
   * while you back up, and the refusal says it once more if you drop the pin
   * too soon.
   */
  function reads(reading) {
    const parts = [];
    if (reading.along > 0.05) parts.push(`${feet(reading.along)} ft to go`);
    else if (reading.along < -0.05) parts.push(`${feet(-reading.along)} ft past`);
    // Which side of the eye the ball is, as the driver facing forward sees it.
    if (Math.abs(reading.side) > 0.05) parts.push(`${feet(Math.abs(reading.side))} ft too far ${reading.side > 0 ? 'right' : 'left'}`);
    const degrees = Math.round((reading.angle * 180) / Math.PI);
    if (degrees >= 2) parts.push(`${degrees}° off`);
    if (reading.speed > 0.4) parts.push('still rolling');
    return parts.length ? parts.join(', ') : 'lined up';
  }

  /**
   * From the driver's seat of something with a ball on the back: dropping the
   * pin on whatever you have backed up to, and taking it off again.
   *
   * Hitching is on the press, above getting out, because when a cart is right
   * behind the ball it is the thing you came to do. It is offered whether or
   * not you have lined it up — the prompt says how far off you are — and
   * refused with the same words if you have not. Unhitching lives in the fan:
   * a press that can drop the load is a press that will.
   */
  /**
   * Over the side, one at a time, while you drive.
   *
   * You cannot pick anything up from the driver's seat, and that stands: your
   * hands are on the wheel. Letting go of one of whatever is in them is not
   * picking up, and a person driving with a bag of oranges in their lap can
   * drop one out behind them without stopping. It is the same set-down as
   * anywhere else — `throwing.drop`, on the bus as a drop, seen and heard like
   * one — just behind a moving cart, which is what lets it be a trail.
   *
   * On the press while your hands have something in them, because it is the
   * thing in your hands; getting out is still one hold away.
   */
  function offerDrop(cart) {
    if (!throwing || carry.empty) return [];
    const item = OBJECTS[carry.kind];
    const behind = wheeled.radius(cart) + 0.4;
    return [
      {
        id: 'cart-drop',
        tier: PLACING,
        kind: CART,
        distance: 0.03,
        verb: 'drop',
        what: `the ${item.name.toLowerCase()} over the side (${carry.count} in hand)`,
        run() {
          const kind = carry.kind;
          const variant = carry.variant;
          const claim = carry.claim ?? null;
          if (!carry.spend()) return;
          throwing.drop({
            kind,
            variant,
            claim,
            at: {
              x: cart.position.x - Math.sin(cart.yaw) * behind,
              z: cart.position.z - Math.cos(cart.yaw) * behind,
            },
            actor: 'player',
          });
          autosave.touch();
        },
      },
    ];
  }

  function offerTow(tug) {
    if (!tug.definition.tows) return [];
    const towed = wheeled.towing(tug);
    if (towed) {
      return [
        {
          id: 'cart-unhitch',
          tier: POSSIBLE,
          kind: CART,
          distance: 0.02,
          verb: 'unhitch',
          what: `the ${name(towed)}`,
          run() {
            wheeled.unhitch(towed);
            hud.say(`Unhitched the ${name(towed)}.`, { tone: 'info', seconds: 2 });
            autosave.touch();
          },
        },
      ];
    }
    const ball = wheeled.ball(tug);
    let best = null;
    for (const cart of wheeled.all) {
      if (cart === tug || cart.seats.length || cart.tipped || cart.state !== 'parked') continue;
      if (Math.hypot(cart.position.x - ball.x, cart.position.z - ball.z) > TOW_REACH + wheeled.radius(cart) * 2) continue;
      const reading = wheeled.hitchReading(tug, cart);
      if (!reading || reading.gap > TOW_REACH) continue;
      if (!best || reading.gap < best.reading.gap) best = { cart, reading };
    }
    if (!best) return [];
    const { cart, reading } = best;
    return [
      {
        id: 'cart-hitch',
        tier: PLACING,
        kind: CART,
        distance: reading.gap,
        verb: 'hitch',
        what: `the ${name(cart)} (${reads(reading)})`,
        run() {
          const result = wheeled.hitch(tug, cart);
          if (result.ok) {
            hud.say(`Hitched the ${name(cart)}.`, { tone: 'good', seconds: 2 });
            autosave.touch();
          } else if (result.reading) {
            hud.say(`Not lined up: ${reads(result.reading)}.`, { tone: 'warn', seconds: 2.5 });
          }
        },
      },
    ];
  }

  function offerCart() {
    const active = wheeled.active;
    const out = [];

    if (active && active.seats.length && active.state === 'ridden') {
      return [...riders.offerSeats(active), ...offerTow(active), ...offerDrop(active)];
    }

    if (active) {
      if (active.state === 'ridden') {
        out.push({
          id: 'cart-dismount',
          tier: NEAR,
          kind: CART,
          distance: 0,
          verb: 'step off',
          what: 'the cart',
          run() {
            wheeled.dismount();
            hud.say('Stepped off.', { tone: 'info', seconds: 1.5 });
          },
        });
      } else {
        /*
         * Getting on is a decision and it lives behind the hold.
         *
         * It used to have a key of its own, which is what made it safe: pressing
         * the action key at a pile with a cart in your hands means take the pile,
         * every time, and it stopped meaning that the moment mounting could win
         * the press. A fetch that collected into the cart and then rode off with
         * it delivered nothing, twice, at one rung, and looked from the outside
         * like the delivery rule being broken.
         *
         * Getting *off* is the other way round — while you are on one it is the
         * obvious thing the key should do — so `cart-dismount` stays on the press.
         */
        out.push({
          id: 'cart-ride',
          tier: POSSIBLE,
          kind: CART,
          distance: 0,
          verb: 'ride',
          what: 'the cart',
          run() {
            wheeled.ride(active, { facing: player.yaw });
            hud.say('On it.', { tone: 'info', seconds: 1.5 });
          },
        });
      }
      /*
       * Letting go is a rarer verb than getting on one, so it lives in the fan.
       *
       * That is not a taste. A press that can park the cart is a press that will
       * park the cart, and the thing standing in front of you when you are
       * holding a cart is nearly always something you want to do *with* it.
       */
      out.push({
        id: 'cart-release',
        tier: POSSIBLE,
        kind: CART,
        distance: 0.01,
        verb: 'let go of',
        what: 'the cart',
        run() {
          const was = active.state;
          wheeled.release();
          hud.say(was === 'ridden' ? 'Stepped off.' : 'Parked.', { tone: 'info', seconds: 1.5 });
          autosave.touch();
        },
      });
      return out;
    }

    const near = wheeled.nearest(player.position, { within: CART_REACH });
    if (!near) return out;
    const cart = near.cart;

    /*
     * Facing test, so a cart parked behind you is not louder than the linen rack
     * in front of you — but taken against the whole cart, not its centre.
     *
     * `interaction.js` learned this for boxes: measure a forty foot shelf from
     * its middle and it is only usable opposite its exact middle. A cart is round
     * rather than long, so the same idea comes out as its angular size. Standing
     * three feet off a cart three feet wide, half the view is cart; facing any
     * part of it is facing it. Measured from the centre instead, a step to one
     * side silently dropped the handle prompt and handed the press to whatever
     * was lying on the floor.
     */
    const dx = cart.position.x - player.position.x;
    const dz = cart.position.z - player.position.z;
    const length = Math.hypot(dx, dz);
    const r = wheeled.radius(cart);
    if (length > r) {
      const centre = (dx / length) * rig.forward.x + (dz / length) * rig.forward.z;
      const offBy = Math.acos(Math.min(1, Math.max(-1, centre))) - Math.asin(r / length);
      if (Math.cos(Math.max(0, offBy)) < 0.25) return out;
    }

    // On a drawbar, the only thing to do with it on foot is take it off.
    if (cart.towedBy) {
      out.push({
        id: 'cart-unhitch',
        tier: NEAR,
        kind: CART,
        distance: near.distance,
        at: cart.position,
        focus: `cart:${cart.id}`,
        verb: 'unhitch',
        what: `the ${name(cart)}`,
        run() {
          wheeled.unhitch(cart);
          hud.say(`Unhitched the ${name(cart)}.`, { tone: 'info', seconds: 2 });
          autosave.touch();
        },
      });
      return out;
    }

    if (cart.tipped) {
      out.push({
        id: 'cart-upright',
        tier: NEAR,
        kind: CART,
        distance: near.distance,
        at: cart.position,
        focus: `cart:${cart.id}`,
        verb: 'stand up',
        what: `the ${cart.definition.name.toLowerCase()}`,
        run() {
          wheeled.upright(cart);
          hud.say('Stood it back up.', { tone: 'info', seconds: 1.5 });
          autosave.touch();
        },
      });
      return out;
    }

    // Something with a driver's seat is got into, not pushed. Same cart, same
    // class; the row has a seat in it and the verb follows the row.
    if (cart.seats.some((seat) => seat.id === 'driver')) {
      out.push({
        id: 'cart-drive',
        tier: NEAR,
        kind: CART,
        distance: near.distance,
        at: cart.position,
        focus: `cart:${cart.id}`,
        verb: 'drive',
        what: `the ${cart.definition.name.toLowerCase()}`,
        run() {
          if (wheeled.ride(cart)) hud.say('In.', { tone: 'info', seconds: 1.5 });
        },
      });
      return out;
    }

    out.push({
      id: 'cart',
      tier: NEAR,
      kind: CART,
      distance: near.distance,
      at: cart.position,
      focus: `cart:${cart.id}`,
      verb: 'take',
      what: `the ${cart.definition.name.toLowerCase()}`,
      run() {
        /*
         * What is in your arms goes on the cart when it fits, because taking a
         * handle is two hands either way. Left in your arms it was out of
         * sight: the screen, reading the empty cart, sent a new starter back
         * to the shelf for the round they were holding, all afternoon.
         */
        const fits =
          !carry.empty &&
          (cart.hold.empty || (cart.hold.kind === carry.kind && cart.hold.variant === carry.variant)) &&
          cart.hold.room(carry.kind, carry.variant) >= carry.count;
        const moved = fits ? { kind: carry.kind, variant: carry.variant, count: carry.count, claim: carry.claim } : null;
        const took = wheeled.grab(cart);
        if (took && moved) {
          cart.hold.add(moved.kind, moved.variant, moved.count);
          if (moved.claim) cart.hold.claim = moved.claim;
          carry.clear();
          const name = OBJECTS[moved.kind]?.name?.toLowerCase() ?? moved.kind;
          hud.say(`Took the handle, with your ${moved.count === 1 ? name : `${moved.count} × ${name}`} on it.`, { tone: 'info', seconds: 2 });
        } else hud.say('Took the handle.', { tone: 'info', seconds: 1.5 });
        autosave.touch();
      },
    });

    /*
     * Moving a load between a cart and your arms, in whichever direction makes
     * sense. Rarely needed — you set straight off the cart — but the three-foot
     * doors mean there are rooms the cart cannot follow you into, which is why it
     * is in the fan rather than gone.
     */
    /*
     * Moving a load between a cart and your arms is the rarest verb a cart has,
     * and it has to be in the fan rather than on the press for a reason that cost
     * a whole fetch table to find: standing at a pile on a dolly, picking the
     * pile up and then pressing again put it straight back on the dolly. You
     * cannot collect anything from a cart with a verb that returns it.
     */
    const load = cart.hold.count
      ? `${cart.hold.count} × ${OBJECTS[cart.hold.kind].name.toLowerCase()}`
      : 'nothing';
    if (!carry.empty) {
      out.push({
        id: 'cart-load',
        tier: POSSIBLE,
        kind: CART,
        distance: near.distance + 0.01,
        verb: 'load',
        what: `your arms onto the cart (${load} on it)`,
        run() {
          const moved = cart.hold.take(carry.kind, carry.variant);
          if (moved.ok) {
            carry.clear();
            hud.say('Loaded onto the cart.', { tone: 'info', seconds: 1.5 });
          }
          autosave.touch();
        },
      });
    } else if (!cart.hold.empty) {
      out.push({
        id: 'cart-unload',
        tier: POSSIBLE,
        kind: CART,
        distance: near.distance + 0.01,
        verb: 'take a load off',
        what: `the cart (${load} on it)`,
        run() {
          carry.take(cart.hold.kind, cart.hold.variant);
          hud.say('Took a load off the cart.', { tone: 'info', seconds: 1.5 });
          autosave.touch();
        },
      });
    }
    return out;
  }

  return { offerCart, CART_REACH };
}
